import {
  AfterContentInit,
  AfterViewInit,
  Component,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { combineLatest, map } from 'rxjs';
import { AuthRepository, UserRoles } from 'app/state/auth.repository';
import { AuthService } from 'app/state/auth.service';
import { UiRepository } from 'app/state/ui.repository';
import { EnvState } from './modules/shared/helpers/env-state';
import { ConnectionService } from './modules/shared/services/connection.service';
import { TranslocoService } from '@jsverse/transloco';
import { DataService, VideUploadData } from './shared/data.service';
import { FireBaseMessagingService } from './helpers/services/fireBaseMessagingService';
import { GeneralDataRepository } from './helpers/repository/general-data.repository';
import { TenantSyncService } from './api/services/tenant-sync.service';
import { LocalStorageService } from './api/services/local-storage-service';
import { NotificationService } from './api/services/notificationService';
import { NotificationsService } from './state/notifications.service';
import { NotificationsRepository } from './state/notifications.repository';
import { ProfilerService } from './shared/profiler-service';
import { SwUpdate } from '@angular/service-worker';
import { RouteInterceptorService } from './shared/deep-link-interceptor';
import { UserSessionService } from './api/services/userSession.service';
import { readStoredLang, syncLangForUser } from './helpers/uiLanguage';

declare var document: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy, AfterContentInit {
  darkModeSubscription = this.ui.isDarkMode$.subscribe((val) =>
    this.switchClass('html', 'dark-mode', val),
  );

  // sessionRefreshSubscription = this.auth.autoRefresh().subscribe(() => {
  //   console.info('Session refreshed');
  // });

  sessionExpiredSubscription = this.authRepo.isExpired$.subscribe(() => {
    if (!this.router.url.startsWith('/signin')) {
      this.ui.setSessionOver();
    }
  });

  // Body class names kept as `menu-open` / `menu-expanded` for compatibility
  // with vendor admin-theme CSS — see UiRepository for the full rationale.
  mobileDrawerSubscription = this.ui.isMobileDrawerOpen$.subscribe((val) =>
    this.switchClass('body', 'menu-open', val),
  );

  sidebarExpandedSubscription = this.ui.isSidebarExpanded$.subscribe((val) =>
    this.switchClass('body', 'menu-expanded', val),
  );

  userRoleSubscription = this.authRepo.roles$.subscribe((roles) => {
    let body = document.getElementsByTagName('body')[0];
    body.classList.forEach((element: any) => {
      if (element.startsWith('user-role-')) {
        body.classList.remove(element);
      }
    });
    roles.forEach((role) =>
      body.classList.add(`user-role-${role.toLocaleLowerCase()}`),
    );
  });

  isSessionRefreshModalDisplayed$ = combineLatest([
    this.ui.isSessionOver$,
    this.connection.isOnline$,
  ]).pipe(map(([sesionOver, online]) => sesionOver && online));

  features = this.env.features;
  videoUploadSub: any;
  videoUploadData?: VideUploadData;
  private routeStartTime = 0;
  private routeTimingSubscribed = false;

  constructor(
    private ui: UiRepository,
    private auth: AuthService,
    private authRepo: AuthRepository,
    private genDataRepo: GeneralDataRepository,
    private router: Router,
    public env: EnvState,
    private dataService: DataService,
    private connection: ConnectionService,
    private service: TranslocoService,
    private fireBaseService: FireBaseMessagingService,
    private tenantSyncService: TenantSyncService,
    private storage: LocalStorageService,
    private notificationsService: NotificationsService,
    private notificationsRepo: NotificationsRepository,
    private profilerService: ProfilerService,
    private swUpdate: SwUpdate,
    private routeInterceptor: RouteInterceptorService,
    private userSessionService: UserSessionService,
  ) {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate();

      this.swUpdate.versionUpdates.subscribe((event) => {
        console.log(event);
        if (event.type === 'VERSION_READY') {
          window.location.reload();
        }
        // A wedged worker (its cache no longer matches what the server
        // serves — e.g. after a deploy that dropped old hashed files) fails
        // every install and would serve stale code forever. Unregister and
        // reload once so the browser fetches fresh from the network and
        // installs a clean worker. The session flag stops a reload loop when
        // the failure is persistent.
        if (
          event.type === 'VERSION_INSTALLATION_FAILED' &&
          !sessionStorage.getItem('swRecovered_v1')
        ) {
          sessionStorage.setItem('swRecovered_v1', 'true');
          navigator.serviceWorker
            ?.getRegistrations()
            .then((rs) => Promise.all(rs.map((r) => r.unregister())))
            .then(() => window.location.reload())
            .catch(() => {});
        }
      });
    }

    this.videoUploadSub = this.dataService.videUploadData$.subscribe((x) => {
      if (x.isInProgress) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflowY = 'scroll';
      }
      this.videoUploadData = x;
    });

    this.genDataRepo.allGeneralData$.subscribe((x) => {
      const trackingEnabled =
        x?.activeUserData?.isPerformanceTrackingEnabled === true;
      // MiniProfilerInterceptor and ProfilerService read this flag: it gates the
      // extra profile fetch per API call and the batched posts to /api/profiler.
      this.profilerService.trackingEnabled = trackingEnabled;

      if (trackingEnabled && !this.routeTimingSubscribed) {
        // Once only: the general data is re-emitted on every sidebar reload, and
        // each further subscription would record every navigation again.
        this.routeTimingSubscribed = true;
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationStart) {
            this.routeStartTime = performance.now();
          }

          if (event instanceof NavigationEnd && this.routeStartTime) {
            requestAnimationFrame(() => {
              const duration = performance.now() - this.routeStartTime;

              this.profilerService.profilesFront.push({
                frontType: '[Routing]',
                frontActionName: event.urlAfterRedirects,
                frontAction: 'loaded',
                duration: duration.toFixed(2),
              });
            });
          }
        });
      }
    });
  }

  private baseUrlPattern = /^https:\/\/nybolig\.igntr\.me(\/.*)?$/;

  enable365 = false;

  ngOnInit() {
    this.enable365 = this.baseUrlPattern.test(document.location.href);

    this.fireBaseService.requestPermission();
    this.fireBaseService.listen();

    const isMobile = window.innerWidth <= 768;
    const deviceType = isMobile ? 'Mobile' : 'Desktop';
    this.userSessionService.createSessionWithLocation().subscribe();

    const localTenantId = localStorage.getItem('activeTenantId');
    this.auth.getActiveTenant().subscribe((tenantId) => {
      if (
        tenantId &&
        localTenantId != tenantId &&
        tenantId != '00000000-0000-0000-0000-000000000000'
      ) {
        localStorage.setItem('activeTenantId', tenantId);
        this.storage.setTenantId(tenantId);
        this.genDataRepo.updateAllGeneralData(true);
      }
    });

    this.authRepo.isImpersonating$.subscribe((x) => {
      this.auth.getUserLang().subscribe({
        next: (x) => {
          // Only sync UI language from the server profile when the user
          // hasn't already made a manual choice (the signin language
          // switcher writes to localStorage). Otherwise their selection
          // gets silently overridden every time this subscription emits.
          this.service.setActiveLang(
            syncLangForUser(this.authRepo.getId(), x.language),
          );
        },
        error: (err) => {
          // On error (e.g. unauthenticated on /signin), fall back to
          // English only if no preference is stored — never clobber the
          // user's manual selection.
          this.service.setActiveLang(readStoredLang());
        },
      });
    });
  }

  ngAfterContentInit() {
    this.notificationsService.loadAllCount().subscribe((x) => {
      this.notificationsRepo.notificationCount$.next(x);
    });
  }

  ngOnDestroy(): void {
    this.darkModeSubscription.unsubscribe();
    // this.sessionRefreshSubscription.unsubscribe();
    this.sessionExpiredSubscription.unsubscribe();
    this.mobileDrawerSubscription.unsubscribe();
    this.sidebarExpandedSubscription.unsubscribe();
    this.userRoleSubscription.unsubscribe();
    this.videoUploadSub.unsubscribe();
  }

  switchClass(element: string, className: string, enabled: boolean) {
    let elem = document.getElementsByTagName(element)[0];
    if (enabled) {
      elem.classList.add(className);
    } else {
      elem.classList.remove(className);
    }
  }

  resolveActionTitle(relativeUrl: string, body: any, method: string) {
    const urlSegments = relativeUrl.split('/');
    const urlSegmentsCount = urlSegments.length;
    const lastSegment = urlSegments[urlSegmentsCount - 1];
    if (lastSegment === 'events') {
      switch (body.type) {
        case 'DayStart':
          return $localize`:Offline event - Day started:Day started`;
        case 'DayEnd':
          return $localize`:Offline event - Day ended:Day ended`;
        case 'Pause':
          return $localize`:Offline event - Pause added:Pause added`;
        case 'TripStart':
          return $localize`:Offline event - Trip started:Trip started`;
        case 'TripEnd':
          return $localize`:Offline event - Trip ended:Trip ended`;
        case 'Unload':
          return $localize`:Offline event - Cargo unloaded:Cargo unloaded`;
      }
    }
    if (lastSegment === 'settings') {
      return $localize`:Offline event - Personal settings updated:Personal settings updated`;
    }
    const firstSegment = urlSegments[0];
    if (urlSegmentsCount === 1 && firstSegment === 'trips') {
      return $localize`:Offline event - Trip initialized:Trip initialized`;
    } else if (urlSegmentsCount === 1 && firstSegment === 'workdays') {
      return $localize`:Offline event - Day initialized:Day initialized`;
    }
    let actionNameTitleCase =
      firstSegment.charAt(0).toUpperCase() + firstSegment.substring(1);
    if (firstSegment === 'workdayevents') {
      actionNameTitleCase = $localize`:Offline event - Workday event added or edited:Workday event`;
    } else if (firstSegment === 'tripevents') {
      actionNameTitleCase = $localize`:Offline event - Trip event added or edited:Trip event`;
    }
    return `${actionNameTitleCase}: ${method === 'POST' ? 'add' : 'edit'}`;
  }
}
